import type { AvatarResponse } from '@innovationdepartment/proxima-sdk-axios';
import { useShowSpinner } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CastingMetadata } from '../avatarManagement.types';
import useAvatarManagementApi from '../useAvatarManagementApi';
import useMetadataDisplay from '../useMetadataDisplay';
import AvatarsView from './Avatars.View';
import useAvatarFilters from './useAvatarFilters';

/** Read-only browser for the shared avatar library. */
const AvatarsContainer = () => {
  const api = useAvatarManagementApi();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<CastingMetadata | null>(null);
  const [library, setLibrary] = useState<AvatarResponse[]>([]);

  useShowSpinner({ show: loading });
  const getDisplay = useMetadataDisplay(metadata);

  const apiRef = useRef(api);
  apiRef.current = api;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const [metadataResult, libraryResult] = await Promise.all([
      apiRef.current.getMetadata(),
      apiRef.current.getAvatarLibrary()
    ]);

    const error = metadataResult.error ?? libraryResult.error;
    if (error != null) {
      setLoadError(error);
      setLoading(false);
      return;
    }

    setMetadata(metadataResult.data ?? null);
    setLibrary(libraryResult.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filters = useAvatarFilters(library);

  return (
    <AvatarsView
      loading={loading}
      loadError={loadError}
      totalCount={library.length}
      avatars={filters.pageAvatars}
      totalPages={filters.totalPages}
      currentPage={filters.currentPage}
      onPageChange={filters.setPage}
      getDisplay={getDisplay}
      ageBounds={filters.ageBounds}
      ageRange={filters.ageRange}
      onAgeRangeChange={filters.setAgeRange}
      genderOptions={metadata?.genders ?? []}
      selectedGenders={filters.selectedGenders}
      onToggleGender={filters.toggleGender}
      sortKey={filters.sortKey}
      onSortChange={filters.setSortKey}
      onRetry={load}
    />
  );
};

export default AvatarsContainer;
